export interface Ingredient {
  /** Key the user picked or "__custom__" for free-text */
  id: string;
  /** Display label */
  label: string;
}

export interface IngredientCategory {
  key: keyof Ritual;
  legend: string;
  hint: string;
  options: Ingredient[];
  allowCustom: boolean;
}

export interface Ritual {
  title: string;
  intent: string;
  setting: string;
  thresholdObject: string;
  openingWords: string;
  posture: string;
  duration: string;
  closing: string;
}

export const INTENTS: Ingredient[] = [
  { id: 'devotion', label: 'devotion' },
  { id: 'gratitude', label: 'gratitude' },
  { id: 'threshold', label: 'a threshold to begin' },
  { id: 'settling', label: 'settling before sleep' },
  { id: 'preparation', label: 'preparation for a scene' },
  { id: 'repair', label: 'repair after difficulty' },
  { id: 'celebration', label: 'celebration of a milestone' },
];

export const SETTINGS: Ingredient[] = [
  { id: 'doorway', label: 'at a doorway' },
  { id: 'chosen-room', label: 'in a chosen room' },
  { id: 'mirror', label: 'before a mirror' },
  { id: 'window', label: 'at a window' },
  { id: 'wherever', label: 'wherever you are when the hour arrives' },
];

export const THRESHOLD_OBJECTS: Ingredient[] = [
  { id: 'candle', label: 'a lit candle' },
  { id: 'ribbon', label: 'a ribbon tied at the wrist' },
  { id: 'coin', label: 'a small coin held in the palm' },
  { id: 'jewelry', label: 'a piece of jewelry put on or removed' },
  { id: 'water', label: 'a glass of water poured slowly' },
  { id: 'incense', label: 'a stick of incense, just lit' },
];

export const OPENING_WORDS: Ingredient[] = [
  { id: 'here', label: 'i am here' },
  { id: 'attention', label: 'i give my attention' },
  { id: 'belong', label: 'i belong to this moment' },
  { id: 'arrive', label: 'i arrive' },
  { id: 'devoted', label: 'i am devoted' },
];

export const POSTURES: Ingredient[] = [
  { id: 'kneeling', label: 'kneeling' },
  { id: 'standing', label: 'standing still, eyes closed' },
  { id: 'sitting', label: 'sitting with hands open in the lap' },
  { id: 'lying', label: 'lying down with hands at the sides' },
  { id: 'heart', label: 'hands on the heart' },
];

export const DURATIONS: Ingredient[] = [
  { id: 'breath', label: 'one full breath' },
  { id: 'minute', label: 'one minute' },
  { id: 'three', label: 'three minutes' },
  { id: 'ten', label: 'ten minutes' },
  { id: 'until', label: 'until the candle is half-burned' },
];

export const CLOSINGS: Ingredient[] = [
  { id: 'extinguish', label: 'extinguish the candle slowly' },
  { id: 'untie', label: 'untie the ribbon' },
  { id: 'set-away', label: 'set the object away with both hands' },
  { id: 'bow', label: 'bow' },
  { id: 'thanks', label: 'speak a single thank you' },
];

export const CATEGORIES: IngredientCategory[] = [
  {
    key: 'intent',
    legend: 'intention',
    hint: 'what is this ritual for?',
    options: INTENTS,
    allowCustom: true,
  },
  {
    key: 'setting',
    legend: 'setting',
    hint: 'where will it happen?',
    options: SETTINGS,
    allowCustom: true,
  },
  {
    key: 'thresholdObject',
    legend: 'threshold object',
    hint: 'a small object that marks the beginning.',
    options: THRESHOLD_OBJECTS,
    allowCustom: true,
  },
  {
    key: 'openingWords',
    legend: 'opening words',
    hint: 'said aloud, or in the mind.',
    options: OPENING_WORDS,
    allowCustom: true,
  },
  {
    key: 'posture',
    legend: 'posture or action',
    hint: 'what does the body do?',
    options: POSTURES,
    allowCustom: true,
  },
  {
    key: 'duration',
    legend: 'duration',
    hint: 'how long does it hold?',
    options: DURATIONS,
    allowCustom: true,
  },
  {
    key: 'closing',
    legend: 'closing gesture',
    hint: 'how does it end?',
    options: CLOSINGS,
    allowCustom: true,
  },
];

export interface SavedRitual {
  id: string;
  ritual: Ritual;
  composed: string;
  createdAt: string;
}

export const DEFAULT_RITUAL: Ritual = {
  title: '',
  intent: 'devotion',
  setting: 'at a doorway',
  thresholdObject: 'a lit candle',
  openingWords: 'i am here',
  posture: 'kneeling',
  duration: 'three minutes',
  closing: 'extinguish the candle slowly',
};
